import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertTriangle,
  CreditCard,
  MapPin,
  Package,
  PieChart as PieIkon,
  Plus,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Buton, Iskelet, Kart, KartBaslik, KartIcerik, cn } from '@emanetx/ui';
import { useOturumStore } from './oturum.js';

const MARKA = '#FFB71B';

export function SayfaPanel() {
  const { t } = useTranslation('admin');
  const kullanici = useOturumStore((s) => s.kullanici);
  const tarih = new Date().toLocaleString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {t('panel.karsilama')}, {kullanici?.adSoyad?.split(' ')[0] ?? '—'} 👋
          </h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">{tarih}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        <KpiKarti baslik={t('panel.aktifKiralamalar')} deger="—" ikon={Package} tema="marka" />
        <KpiKarti baslik={t('panel.bugunkuCiro')} deger="—" ikon={TrendingUp} tema="yesil" />
        <KpiKarti baslik={t('panel.dolulukOrani')} deger="—" ikon={PieIkon} tema="mavi" />
        <KpiKarti baslik={t('panel.onlineDolap')} deger="—" ikon={Activity} tema="turuncu" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Kart className="min-h-[320px] lg:col-span-2">
          <KartBaslik>
            <h2 className="text-base font-semibold">{t('panel.son30GunCiro')}</h2>
          </KartBaslik>
          <KartIcerik>
            <BosCizgiGrafik bos={t('panel.veriYok')} />
          </KartIcerik>
        </Kart>
        <Kart className="min-h-[320px]">
          <KartBaslik>
            <h2 className="text-base font-semibold">{t('panel.noktaDagilim')}</h2>
          </KartBaslik>
          <KartIcerik>
            <BosPastaGrafik bos={t('panel.veriYok')} />
          </KartIcerik>
        </Kart>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Kart className="lg:col-span-2">
          <KartBaslik>
            <h2 className="text-base font-semibold">{t('panel.canliFeed')}</h2>
          </KartBaslik>
          <KartIcerik>
            <BosCanliFeed bos={t('panel.veriYok')} />
          </KartIcerik>
        </Kart>
        <Kart>
          <KartBaslik>
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <AlertTriangle size={16} className="text-marka-500" /> {t('panel.uyarilar')}
            </h2>
          </KartBaslik>
          <KartIcerik className="space-y-3">
            <Iskelet className="h-12" />
            <Iskelet className="h-12" />
            <Iskelet className="h-12" />
          </KartIcerik>
        </Kart>
      </div>

      <div>
        <h2 className="text-fume-500 dark:text-fume-400 mb-3 text-xs font-semibold uppercase tracking-wider">
          {t('panel.hizliAksiyon')}
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <HizliAksiyon ikon={MapPin} etiket={t('panel.yeniNokta')} />
          <HizliAksiyon ikon={Users} etiket={t('panel.yeniKullanici')} />
          <HizliAksiyon ikon={CreditCard} etiket={t('panel.yeniKupon')} />
          <HizliAksiyon ikon={Zap} etiket={t('panel.raporlar')} />
        </div>
      </div>
    </div>
  );
}

function KpiKarti({
  baslik,
  deger,
  ikon: Ikon,
  tema,
}: {
  baslik: string;
  deger: string;
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  tema: 'marka' | 'yesil' | 'mavi' | 'turuncu';
}) {
  const arkaPlan = {
    marka: 'bg-marka-100 text-marka-700 dark:bg-marka-900/30 dark:text-marka-300',
    yesil: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    mavi: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
    turuncu: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  }[tema];

  return (
    <Kart className="p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <p className="text-fume-500 dark:text-fume-400 text-sm">{baslik}</p>
        <span className={cn('grid h-9 w-9 place-items-center rounded-lg', arkaPlan)}>
          <Ikon size={18} />
        </span>
      </div>
      <p className="mt-3 font-mono text-3xl font-bold tracking-tight">{deger}</p>
      <p className="text-fume-400 mt-1 text-xs">API hazır olunca dolar</p>
    </Kart>
  );
}

function HizliAksiyon({
  ikon: Ikon,
  etiket,
}: {
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  etiket: string;
}) {
  return (
    <Buton stil="cizgili" className="h-auto flex-col gap-2 py-4">
      <Ikon size={20} />
      <span className="text-sm">{etiket}</span>
    </Buton>
  );
}

function BosCizgiGrafik({ bos }: { bos: string }) {
  const veri: Array<{ gun: string; tutar: number }> = [];
  if (veri.length === 0) return <BosDurum mesaj={bos} ikon={TrendingUp} />;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={veri}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="gun" />
        <YAxis />
        <Tooltip />
        <Line type="monotone" dataKey="tutar" stroke={MARKA} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function BosPastaGrafik({ bos }: { bos: string }) {
  const veri: Array<{ ad: string; deger: number }> = [];
  if (veri.length === 0) return <BosDurum mesaj={bos} ikon={PieIkon} />;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <PieChart>
        <Pie data={veri} dataKey="deger" nameKey="ad" outerRadius={80} fill={MARKA} />
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

function BosCanliFeed({ bos }: { bos: string }) {
  return <BosDurum mesaj={bos} ikon={Activity} />;
}

function BosDurum({
  mesaj,
  ikon: Ikon,
}: {
  mesaj: string;
  ikon: React.ComponentType<{ size?: number; className?: string }>;
}) {
  return (
    <div className="text-fume-400 dark:text-fume-500 grid min-h-[200px] place-items-center gap-3">
      <Ikon size={36} />
      <p className="text-sm">{mesaj}</p>
      <Buton stil="cizgili" boyut="kucuk">
        <Plus size={14} /> İlk kaydı oluştur
      </Buton>
    </div>
  );
}
